import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Settings,
  ChevronRight,
  ArrowLeft,
  CreditCard,
  Printer,
  ScrollText,
  Store,
  Users,
  Bell,
  Shield,
  Palette,
  Percent,
  Link2,
  CalendarClock
} from 'lucide-react';

/**
 * Settings Layout Component
 *
 * Wraps all settings sub-pages with consistent breadcrumb navigation.
 * Compact single-line header with back button, breadcrumb, title, and action buttons.
 */

// Breadcrumb configuration - maps paths to labels and icons
const BREADCRUMB_CONFIG = {
  '/settings': {
    label: 'Settings',
    icon: Settings,
    parent: null,
  },
  '/settings/branch': {
    label: 'Branch Information',
    icon: Store,
    parent: '/settings',
  },
  '/settings/payment-accounts': {
    label: 'Payment Accounts',
    icon: CreditCard,
    parent: '/settings',
  },
  '/settings/printers': {
    label: 'Printer Configuration',
    icon: Printer,
    parent: '/settings',
  },
  '/settings/receipts': {
    label: 'Receipt Templates',
    icon: ScrollText,
    parent: '/settings',
  },
  '/settings/users': {
    label: 'Users & Roles',
    icon: Users,
    parent: '/settings',
  },
  '/settings/notifications': {
    label: 'Notifications',
    icon: Bell,
    parent: '/settings',
  },
  '/settings/security': {
    label: 'Security',
    icon: Shield,
    parent: '/settings',
  },
  '/settings/appearance': {
    label: 'Appearance',
    icon: Palette,
    parent: '/settings',
  },
  '/settings/tax': {
    label: 'Tax',
    icon: Percent,
    parent: '/settings',
  },
  '/settings/integrations': {
    label: 'Integrations',
    icon: Link2,
    parent: '/settings',
  },
  '/settings/scheduler-status': {
    label: 'Scheduler Status',
    icon: CalendarClock,
    parent: '/settings',
  },
};

// Build breadcrumb trail from current path
function buildBreadcrumbTrail(pathname) {
  const trail = [];
  let currentPath = pathname;

  while (currentPath && BREADCRUMB_CONFIG[currentPath]) {
    const config = BREADCRUMB_CONFIG[currentPath];
    trail.unshift({
      path: currentPath,
      label: config.label,
      icon: config.icon,
    });
    currentPath = config.parent;
  }

  return trail;
}

/**
 * SettingsLayout - Wrapper component for settings sub-pages
 *
 * @param {Object} props
 * @param {ReactNode} props.children - The settings page content
 * @param {ReactNode} props.actions - Action buttons to display in header (Add, Refresh, etc.)
 * @param {string} props.title - Page title (optional, auto-detected from path)
 */
export default function SettingsLayout({ children, actions, title }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Build breadcrumb trail
  const trail = buildBreadcrumbTrail(location.pathname);

  // Get title and icon from config if not provided
  const pageTitle = title || (trail.length > 0 ? trail[trail.length - 1].label : 'Settings');
  const PageIcon = trail.length > 0 ? trail[trail.length - 1].icon : Settings;

  // Navigation handlers
  const handleBack = () => {
    navigate('/settings');
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Compact Sticky Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left Side - Back, Breadcrumb, Title */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Back Button */}
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all flex-shrink-0"
              title="Back to Settings"
            >
              <ArrowLeft size={18} />
            </button>

            {/* Divider */}
            <div className="h-5 w-px bg-slate-200 flex-shrink-0" />

            {/* Breadcrumb + Title */}
            <nav className="flex items-center gap-2 text-sm min-w-0">
              {/* Settings link */}
              <button
                onClick={() => handleNavigate('/settings')}
                className="text-slate-500 hover:text-slate-900 transition-colors flex-shrink-0"
              >
                Settings
              </button>

              <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />

              {/* Current page title with icon */}
              <div className="flex items-center gap-2 min-w-0">
                <PageIcon size={16} className="text-[#C45500] flex-shrink-0" />
                <span className="font-semibold text-slate-900 truncate">{pageTitle}</span>
              </div>
            </nav>
          </div>

          {/* Right Side - Action Buttons */}
          {actions && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>

      {/* Page Content - Full Width */}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
}

// Export config for external use
export { BREADCRUMB_CONFIG, buildBreadcrumbTrail };
